import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Loader2 } from 'lucide-react';
import { usePreferences } from '../context/PreferencesContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MMDPage = () => {
  const navigate = useNavigate();
  const { preferences, updatePreferences } = usePreferences();
  const { isAuthenticated } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/mmd/locations/public`);
      // Store full location data including grades for filtering
      setLocations(response.data.map(loc => ({
        id: loc.city,
        name: loc.city,
        fullName: loc.name,
        code: loc.code,
        grades: loc.grades || [] // Include grades for filtering
      })));
    } catch (error) {
      console.error('Failed to fetch MMD locations:', error);
      // Fallback to expanded locations if API fails (no grade filtering possible)
      setLocations([
        { id: 'Mumbai', name: 'Mumbai', grades: [] },
        { id: 'Chennai', name: 'Chennai', grades: [] },
        { id: 'Kolkata', name: 'Kolkata', grades: [] },
        { id: 'Kochi', name: 'Kochi', grades: [] },
        { id: 'Noida', name: 'Noida', grades: [] },
        { id: 'Visakhapatnam', name: 'Visakhapatnam', grades: [] },
        { id: 'Goa', name: 'Goa', grades: [] },
        { id: 'Kandla', name: 'Kandla', grades: [] }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Filter locations based on selected grade
  const filteredLocations = locations.filter(loc => {
    // If no grade selected or location has no grade restrictions, show all
    if (!preferences.grade || loc.grades.length === 0) return true;
    // Only show locations that support the selected grade
    return loc.grades.includes(preferences.grade);
  });

  const handleLocationSelect = async (location) => {
    await updatePreferences({ mmd_location: location.id });
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0A7EA4] mb-4">
            Select MMD Location
          </h1>
          <p className="text-gray-600 text-base">
            Choose your examination center location
            {preferences.grade && (
              <span className="block text-sm text-amber-600 mt-1">
                Showing locations available for {preferences.grade}
              </span>
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {filteredLocations.map((location) => (
            <Card
              key={location.id}
              data-testid={`mmd-${location.id.toLowerCase()}`}
              onClick={() => handleLocationSelect(location)}
              className="cursor-pointer hover-lift border-2 border-gray-200 hover:border-[#0A7EA4] transition-colors"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#0A7EA4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-[#0A7EA4]" />
                </div>
                <h3 className="text-xl font-semibold">{location.name}</h3>
                {location.code && (
                  <p className="text-sm text-gray-500 mt-1">{location.code}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredLocations.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <p>No MMD locations available for {preferences.grade}.</p>
            <p className="text-sm mt-2">Please contact support or select a different grade.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MMDPage;
